import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import 'highlight.js/styles/github.css';

interface MarkdownTextProps {
  content?: string;
  markdown?: string; // Backward compatibility
  color?: string;
}

const MarkdownText: React.FC<MarkdownTextProps> = ({
  content,
  markdown,
  color = 'inherit'
}) => {
  const navigate = useNavigate();

  // Support both 'content' and 'markdown' props for backward compatibility
  const text = content || markdown || '';

  // Handle internal Paiper links
  const handleLinkClick = (href: string, event: React.MouseEvent) => {
    // Check if it's a Paiper internal link
    if (href.includes('Public_Form_')) {
      event.preventDefault();
      const formId = href.split('Public_Form_')[1].split(/[?#]/)[0];
      navigate(`/form/Public_Form_${formId}`);
    }
    // External links will open normally
  };

  return (
    <Box sx={{ color }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Style headings
          h1: ({ node, children, ...props }) => (
            <Typography variant="h4" component="h1" gutterBottom>
              {children}
            </Typography>
          ),
          h2: ({ node, children, ...props }) => (
            <Typography variant="h5" component="h2" gutterBottom>
              {children}
            </Typography>
          ),
          h3: ({ node, children, ...props }) => (
            <Typography variant="h6" component="h3" gutterBottom>
              {children}
            </Typography>
          ),

          // Style links with click handling
          a: ({ node, href, children, ...props }) => (
            <a
              href={href}
              onClick={(e) => handleLinkClick(href || '', e)}
              style={{
                color: '#C47D4E',
                fontWeight: 600,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
              {...props}
            >
              {children}
            </a>
          ),

          // Style code blocks
          code: ({ node, className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match;

            if (isInline) {
              return (
                <code
                  style={{
                    backgroundColor: 'rgba(175, 184, 193, 0.2)',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontFamily: 'monospace',
                  }}
                >
                  {children}
                </code>
              );
            }
            return (
              <code className={className}>
                {children}
              </code>
            );
          },

          // Style blockquotes
          blockquote: ({ node, children, ...props }) => (
            <Box
              component="blockquote"
              sx={{
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                paddingLeft: 2,
                marginLeft: 0,
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              {children}
            </Box>
          ),

          // Style lists
          ul: ({ node, children, ...props }) => (
            <ul style={{ paddingLeft: '20px' }}>
              {children}
            </ul>
          ),
          ol: ({ node, children, ...props }) => (
            <ol style={{ paddingLeft: '20px' }}>
              {children}
            </ol>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownText;